import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";
import Pagination from "../Pagination";

const getInitials = (name) => {
  const words = String(name || "A").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase() || "A";
};

const formatDate = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const EmployeeApplicants = () => {
  const { jobId } = useParams();
  const perPage = 10;
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [applications, setApplications] = useState([]);
  const [meta, setMeta] = useState({});
  const apiUrl = process.env.REACT_APP_BASE_API_URL + `/employer/applications?per_page=${perPage}&page=${page}` + (jobId ? `&job_id=${jobId}` : "") + (refreshKey ? `&_=${refreshKey}` : "");

  const { response, isLoading, error } = useFetch(apiUrl);

  useEffect(() => {
    setApplications(response?.applications?.data ?? []);
    setMeta(response?.applications?.meta ?? {});
  }, [response]);

  const refresh = () => {
    setRefreshKey(Date.now());
  };

  const updateStatus = (application, status) => {
    let formData = new FormData();
    formData.append("status", status);

    apiRequest(`/employer/applications/${application.id}/status`, {
      method: "POST",
      body: formData,
    })
      .then(() => refresh())
      .catch(() => refresh());
  };

  const selectedJobTitle = applications[0]?.job?.title;

  return (
    <EmployeePageShell
      active="applicants"
      title={selectedJobTitle ? `${selectedJobTitle} Applicants` : "Applicants"}
      text={selectedJobTitle ? "Candidates who applied for this job post." : "A simple view of recent candidate applications."}
    >
      <div className="ls-widget">
        <div className="widget-title">
          <h4>{selectedJobTitle ? "Job Applicants" : "Recent Applicants"}</h4>
          {jobId && <Link to="/employee-manage-jobs" className="theme-btn btn-style-three">Back to Jobs</Link>}
        </div>
        <div className="widget-content">
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

          <div className="employee-applicant-list">
            {!error && !isLoading && applications.map((item) => (
              <div className="employee-applicant-item employee-applicant-item-dynamic" key={item.id}>
                <div className="employee-applicant-avatar">
                  {item.candidate_profile?.photo_url ? (
                    <img src={item.candidate_profile.photo_url} alt={item.name} />
                  ) : (
                    <span>{getInitials(item.name)}</span>
                  )}
                </div>
                <div className="employee-applicant-content">
                  <strong>{item.name}</strong>
                  <span>Applied for {item.job?.title ?? "Job"} on {formatDate(item.created_at)}</span>
                  <small>{item.email}{item.phone ? ` - ${item.phone}` : ""}</small>
                  {item.cover_letter && <p>{item.cover_letter}</p>}
                  {item.resume_url && (
                    <div className="employee-applicant-resume-actions">
                      <span>{item.resume_original_name ?? "Resume attached"}</span>
                      <a href={item.resume_url} target="_blank" rel="noreferrer">Open Resume</a>
                      {item.resume_download_url && (
                        <a href={item.resume_download_url} target="_blank" rel="noreferrer">Download</a>
                      )}
                    </div>
                  )}
                </div>
                <div className="employee-applicant-status">
                  <span>{item.status}</span>
                  <select value={item.status} onChange={(e) => updateStatus(item, e.target.value)}>
                    <option value="submitted">Submitted</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            ))}

            {!error && !isLoading && applications.length === 0 && (
              <div className="employee-empty-state">
                No applicants found for this job.
              </div>
            )}
          </div>

          {!error && !isLoading && meta?.total > 0 && (
            <i className="d-block text-center">({meta.total} applications found)</i>
          )}
          {!error && !isLoading && <Pagination meta={meta} onPageChange={setPage} label="Employer applicants pagination" />}
        </div>
      </div>
    </EmployeePageShell>
  );
};

export default EmployeeApplicants;
